/**
 * v1.5 P4：笔记排行榜 — 哪篇笔记 ROI 高/线索多/出单多
 *
 * 数据源：
 *   - xhs_note_metrics（聚光笔记日报表，cron 每日 03:00 拉）
 *   - marketing_leads.note_id（反查 CRM 真实线索数 + 加微数）
 *   - orders（反查出单 + 营收 → ROI）
 *
 * 用途：运营看哪篇笔记效果好，复制爆款
 */
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DatePicker, Select, Tag } from 'antd';
import dayjs from 'dayjs';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, TableWrap, Pill } from '../../components/ui';

interface NoteRow {
  noteId: string;
  noteTitle?: string | null;
  noteLink?: string | null;
  spend: number;
  impression: number;
  click: number;
  ctr: number;
  xhsLeads: number;       // 小红书侧统计
  crmLeads: number;       // 我们侧（marketing_leads.note_id 反查）
  wechatAdded: number;    // 加微成功数
  wechatRate: number;     // 加微成功率
  orders: number;         // 出单数
  revenue: number;        // 营收
  roi: number;            // 营收 / 花费
  costPerLead: number | null;
  costPerOrder: number | null;
}

type Sort = 'spend' | 'leads' | 'impression' | 'click';

export function XhsNoteLeaderboardPage() {
  const [from, setFrom] = useState<dayjs.Dayjs>(dayjs().subtract(30, 'day'));
  const [to, setTo] = useState<dayjs.Dayjs>(dayjs());
  const [sort, setSort] = useState<Sort>('spend');

  const { data: rows, isLoading } = useQuery<NoteRow[]>({
    queryKey: ['xhs-note-leaderboard', from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'), sort],
    queryFn: () => $.get('/marketing/xhs-notes/leaderboard', {
      from: from.format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
      sort,
      limit: 50,
    }),
  });

  // 汇总（TOP 50 的）
  const totals = useMemo(() => {
    const items = rows ?? [];
    return {
      spend: items.reduce((s, r) => s + r.spend, 0),
      impression: items.reduce((s, r) => s + r.impression, 0),
      click: items.reduce((s, r) => s + r.click, 0),
      crmLeads: items.reduce((s, r) => s + r.crmLeads, 0),
      wechatAdded: items.reduce((s, r) => s + r.wechatAdded, 0),
      orders: items.reduce((s, r) => s + r.orders, 0),
      revenue: items.reduce((s, r) => s + r.revenue, 0),
    };
  }, [rows]);

  const totalRoi = totals.spend > 0 ? totals.revenue / totals.spend : 0;
  const totalCtr = totals.impression > 0 ? totals.click / totals.impression : 0;
  const totalWechatRate = totals.crmLeads > 0 ? totals.wechatAdded / totals.crmLeads : 0;

  return (
    <>
      <PageHeader
        breadcrumb={['投流', '笔记排行榜']}
        title="📝 笔记排行榜"
        subtitle="按花费 / 线索 / 曝光 / 点击排序，看哪篇笔记 ROI 高（数据来源：聚光离线报表 + CRM 线索 + 订单）"
      />
      <div className="work-content">
        {/* 筛选条 */}
        <HomeCard title="筛选">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13 }}>日期范围：</span>
            <DatePicker value={from} onChange={v => v && setFrom(v)}
                        format="YYYY-MM-DD" disabledDate={d => d.isAfter(dayjs())} />
            <span>~</span>
            <DatePicker value={to} onChange={v => v && setTo(v)}
                        format="YYYY-MM-DD" disabledDate={d => d.isAfter(dayjs())} />
            <span style={{ marginLeft: 16, fontSize: 13 }}>排序：</span>
            <Select value={sort} onChange={(v: Sort) => setSort(v)} style={{ width: 140 }}
                    options={[
                      { value: 'spend', label: '按花费' },
                      { value: 'leads', label: '按线索数' },
                      { value: 'impression', label: '按曝光' },
                      { value: 'click', label: '按点击' },
                    ]} />
          </div>
        </HomeCard>

        {/* 汇总卡片 — v1.5 真修字溢出：min-width:0 + flex-wrap 让大数字 + Tag 自动换行 */}
        <HomeCard title="汇总（TOP 50 笔记）">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>累计花费</div>
              <div style={{ fontSize: 18, fontWeight: 600, wordBreak: 'break-word', fontVariantNumeric: 'tabular-nums' }}>
                ¥{Math.round(totals.spend).toLocaleString()}
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>累计营收 / ROI</div>
              <div style={{ fontSize: 18, fontWeight: 600, wordBreak: 'break-word', fontVariantNumeric: 'tabular-nums',
                            display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
                <span>¥{Math.round(totals.revenue).toLocaleString()}</span>
                <Tag color={totalRoi >= 2 ? 'green' : totalRoi >= 1 ? 'blue' : 'red'}>
                  ROI {totalRoi.toFixed(2)}x
                </Tag>
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>线索数 / 加微转化率</div>
              <div style={{ fontSize: 18, fontWeight: 600, wordBreak: 'break-word', fontVariantNumeric: 'tabular-nums',
                            display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
                <span>{totals.crmLeads}</span>
                <Tag color={totalWechatRate >= 0.5 ? 'green' : totalWechatRate >= 0.3 ? 'blue' : 'orange'}>
                  加微 {(totalWechatRate * 100).toFixed(1)}%
                </Tag>
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>曝光 / CTR</div>
              <div style={{ fontSize: 18, fontWeight: 600, wordBreak: 'break-word', fontVariantNumeric: 'tabular-nums',
                            display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
                <span>{totals.impression.toLocaleString()}</span>
                <Tag color="default">CTR {(totalCtr * 100).toFixed(2)}%</Tag>
              </div>
            </div>
          </div>
        </HomeCard>

        {/* 排行榜表 */}
        <HomeCard title={`排行榜（按${sort === 'spend' ? '花费' : sort === 'leads' ? '线索' : sort === 'impression' ? '曝光' : '点击'}降序）`}>
          <TableWrap>
            <table className="ck-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>笔记</th>
                  <th style={{ width: 110 }}>花费</th>
                  <th style={{ width: 100 }}>曝光</th>
                  <th style={{ width: 80 }}>点击</th>
                  <th style={{ width: 80 }}>CTR</th>
                  <th style={{ width: 80 }}>线索</th>
                  <th style={{ width: 100 }}>加微转化</th>
                  <th style={{ width: 70 }}>出单</th>
                  <th style={{ width: 120 }}>营收</th>
                  <th style={{ width: 90 }}>ROI</th>
                  <th style={{ width: 90 }}>CPL/CPA</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={12} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</td></tr>
                )}
                {!isLoading && (!rows || rows.length === 0) && (
                  <tr><td colSpan={12} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
                    暂无数据 — 配置完聚光 OAuth + cron 拉到数据后会出现
                  </td></tr>
                )}
                {(rows ?? []).map((r, idx) => (
                  <tr key={r.noteId}>
                    <td>{idx + 1}</td>
                    <td>
                      <div style={{ fontWeight: 500, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.noteTitle ?? r.noteId}
                      </div>
                      {r.noteLink && (
                        <a href={r.noteLink} target="_blank" rel="noopener noreferrer"
                           style={{ fontSize: 11, color: 'var(--primary)' }}>
                          🔗 打开笔记
                        </a>
                      )}
                    </td>
                    <td><span className="money">¥{r.spend.toFixed(0)}</span></td>
                    <td>{r.impression.toLocaleString()}</td>
                    <td>{r.click.toLocaleString()}</td>
                    <td>{(r.ctr * 100).toFixed(2)}%</td>
                    <td>
                      {r.crmLeads}
                      {r.xhsLeads > 0 && r.xhsLeads !== r.crmLeads && (
                        <span style={{ fontSize: 10, color: 'var(--text-3)', marginLeft: 4 }}>
                          (聚光 {r.xhsLeads})
                        </span>
                      )}
                    </td>
                    <td>
                      {r.crmLeads > 0
                        ? <Pill tone={r.wechatRate >= 0.5 ? 'success' : r.wechatRate >= 0.3 ? 'primary' : 'warning'}>
                            {(r.wechatRate * 100).toFixed(0)}% ({r.wechatAdded})
                          </Pill>
                        : '—'}
                    </td>
                    <td>{r.orders}</td>
                    <td><span className="money">¥{r.revenue.toLocaleString()}</span></td>
                    <td>
                      {r.spend > 0
                        ? <Pill tone={r.roi >= 2 ? 'success' : r.roi >= 1 ? 'primary' : 'danger'}>
                            {r.roi.toFixed(2)}x
                          </Pill>
                        : '—'}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {r.costPerLead != null && <div>CPL ¥{r.costPerLead.toFixed(0)}</div>}
                      {r.costPerOrder != null && <div>CPA ¥{r.costPerOrder.toFixed(0)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>
    </>
  );
}
